import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Map from "../../components/common/Map";
import AggregationConfiguration from "../../components/common/AggregationConfiguration";
import { useMapData } from "../../hooks/useMapData";
import { useUrlState } from "../../hooks/useUrlState";
import { BoundingBox, MapGeometry } from "../../services/mapsApi";
import { runOdmJob } from "../../services/odmApi";
import type { ODMJobParams, ODMResult } from "../../services/odmApi";
import { JobError } from "../../services/dataJobs";
import { isDemoJobId, isDemoRouted } from "../../services/demoApi";
import { isDraftMocked } from "../../services/draftMocks";
import type { JobStatusResponse } from "../../services/dataJobs";
import { describeRequestError, isAbortError } from "../../services/http";
import AvailableDatePicker from "../../components/data/AvailableDatePicker";
import AboutThisData, { deliveredDays } from "../../components/data/AboutThisData";
import DataStateNotice from "../../components/data/DataStateNotice";
import ResultCapNotice from "../../components/data/ResultCapNotice";
import JobStatusPanel from "../../components/data/JobStatusPanel";
import SourceBadge from "../../components/data/SourceBadge";
import DraftDataBadge from "../../components/data/DraftDataBadge";
import { useDataWindow } from "../../hooks/useDataWindow";
import { DEFAULT_COUNTRY, normalizeCountry } from "../../config/countries";
import type { CountryCode } from "../../config/countries";
import { GRANULARITY_LABELS, PRODUCT_LABELS, geoCapability, geoTypeLabel, pendingFor, toApiGeoIds } from "../../config/products";
import { pickerPath } from '../../utils/productPaths';
import { asSearch } from '../../utils/navScope';
import { formatDayLabel } from "../../utils/dateWindow";
import { submitBlockReason } from "../../utils/submitGuard";
import { pluralize } from "../../utils/plural";
import { areaCount } from "../../utils/selectionSummary";
import { DEFAULT_ODM_DIRECTION, directionLabel, mergeOdmRows, odmRequestSides } from "../../utils/odmDirection";
import type { OdmDirection } from "../../utils/odmDirection";
import { odmScopeFromState, selectionAreaIds, selectionCountry, selectionGeoType } from "../../utils/odmSelection";
import type { SelectedArea } from "../../utils/odmSelection";
import { odmBackState } from "../../utils/returnState";
import type { OdmHandoff } from "../../utils/returnState";
import { asWindowUtc, pickerDiffersFromJob, submittedFromScope } from "../../utils/submittedWindow";
import type { SubmittedWindow, WindowUtc } from "../../utils/submittedWindow";
import { WebMercatorViewport } from "@deck.gl/core";
import NavBar from "../../components/common/NavBar";
import { fitView } from '../../geo/areaShapes';
import { cellsAtRung, isModelGrid, largestRung, rungsFor } from "../../services/catalogueRequests";

/** How a grid cell size reads: "250 m", "1 km". */
const rungLabel = (rung: string) => {
  const m = Number(/^grid_(\d+)m$/.exec(rung)?.[1]);
  return !Number.isFinite(m) ? rung : m >= 1000 ? `${m / 1000} km` : `${m} m`;
};

// The job preview returns at most this many rows, so a count equal to it is a cut-off, not a total.
const PREVIEW_ROW_LIMIT = 100;

const SAMPLE_DATA: Array<{
  position: [number, number];
  population: number;
  name: string;
}> = [];

/**
 * What the page is opened with. Current state holds `selection` (saved areas of one type), its
 * `geoType` and `geometries`, and on a return trip the `direction`. State from before the single
 * picker held originSelection/destinationSelection and their geometries; odmScopeFromState reads
 * both (see utils/odmSelection.ts).
 */
interface ODMPageState {
  selection?: SelectedArea[];
  geoType?: string;
  geometries?: MapGeometry[];
  direction?: OdmDirection;
  countryCode?: string;
  /** The picker's query string (its country filter), so Back lands on the same list. */
  pickerSearch?: string;
  /** Older state, from the origin and destination pickers. */
  originSelection?: unknown;
  destinationSelection?: unknown;
  originGeometries?: MapGeometry[];
  destinationGeometries?: MapGeometry[];
  singleArea?: boolean;
  /** Set when the preview page sends the person back: the job, its results and its day and grain. */
  preserveState?: boolean;
  jobId?: string;
  jobResults?: ODMResult[];
  jobStatus?: JobStatusResponse;
  preservedDay?: string;
  preservedGranularity?: 'hourly' | 'daily';
  preservedWindowUtc?: WindowUtc;
}

/** A message under the Submit button: what went wrong, in a short title and a sentence. */
interface PageNotice {
  title: string;
  body: string;
}

const TO_FROM_PENDING = 'To / from needs backend support (pending)';

const ODM: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addGridId, removeGridId } = useUrlState();
  // Where the page was opened from: the setup page, or a return trip from the preview.
  const navState = location.state as ODMPageState | null;
  const [selectedPoints, setSelectedPoints] = useState<typeof SAMPLE_DATA>([]);
  const [selectedGrids, setSelectedGrids] = useState<MapGeometry[]>([]);
  const [currentZoom, setCurrentZoom] = useState<number>(10);
  const [viewState, setViewState] = useState<any>(null);

  // ODM-specific state. Seeded from the navigation state, so the very first render already knows
  // the country and area types and asks for the right delivery days.
  const [selection, setSelection] = useState<SelectedArea[]>(() => odmScopeFromState(navState)?.selection ?? []);
  const [requestedDirection, setRequestedDirection] = useState<OdmDirection>(
    () => odmScopeFromState(navState)?.direction ?? DEFAULT_ODM_DIRECTION,
  );
  const [odmGeometries, setOdmGeometries] = useState<any[]>([]);
  const [countryCode, setCountryCode] = useState<CountryCode | null>(
    () => normalizeCountry(navState?.countryCode) ?? selectionCountry(odmScopeFromState(navState)?.selection ?? []),
  );

  // API state management
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [jobResults, setJobResults] = useState<ODMResult[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [submitError, setSubmitError] = useState<PageNotice | null>(null);
  // The day and resolution the job on screen was submitted for. The picker stays live after a job
  // finishes, so results, the preview and the way back use this, never the picker's current value.
  const [submitted, setSubmitted] = useState<SubmittedWindow | null>(() =>
    navState?.preserveState
      ? submittedFromScope({
          day: navState.preservedDay ?? null,
          granularity: navState.preservedGranularity ?? null,
          windowUtc: asWindowUtc(navState.preservedWindowUtc),
        })
      : null,
  );
  const [selectionNotice, setSelectionNotice] = useState<string | null>(null);

  // The area type of the selection (every picked area has the same one) and the ids it holds.
  const geoType = selectionGeoType(selection);
  const areaIds = selectionAreaIds(selection);

  // To / from sends "anywhere" (an empty list) for one end. The catalogue 1.0 data API reads an empty end as every
  // area, as the demo API does; anything else counts trips Between the areas.
  const toFromAvailable =
    isDemoRouted(countryCode) || isDraftMocked(countryCode, 'odm', geoType) || geoCapability(countryCode, 'odm', geoType) !== undefined;
  // A model-grid area is read at one cell size: its largest, or any coarser one.
  const gridRungs = useMemo(() => (isModelGrid(geoType) ? rungsFor(areaIds) : []), [geoType, areaIds]);
  const [chosenRung, setChosenRung] = useState<string | null>(null);
  const rung = chosenRung && gridRungs.includes(chosenRung) ? chosenRung : largestRung(areaIds);
  const rungCells = useMemo(() => (gridRungs.length && rung ? cellsAtRung(areaIds, rung) : null), [gridRungs, rung, areaIds]);
  const direction: OdmDirection = requestedDirection === 'to-from' && !toFromAvailable ? 'between' : requestedDirection;

  // The delivery day and resolution come from what was actually delivered for this country and
  // area type; they are also the only source of the request's time window. With no day chosen
  // the window opens on the latest delivered day, and the picker only offers delivered days.
  const dw = useDataWindow({
    country: countryCode,
    product: 'odm',
    geoType,
    initial: { day: navState?.preservedDay, granularity: navState?.preservedGranularity },
  });
  const blockReason = submitBlockReason({
    dw,
    selectionCount: areaIds.length > 0 ? 1 : 0,
  });

  const pickerMoved = pickerDiffersFromJob(submitted, dw);
  const selectionText = areaCount(areaIds.length, geoType, countryCode);

  const { loading, clearData } = useMapData();

  // The map opens on the selected areas; only while their shapes are loading or missing does it
  // show the selection's country. The country comes from the selection, never from the URL.
  const mapCountry: CountryCode = countryCode ?? DEFAULT_COUNTRY;
  const mapInitialView = useMemo(
    () => fitView(odmGeometries.map((g) => g.geometry), countryCode),
    [odmGeometries, countryCode],
  );

  // The job on screen. Leaving the page, or submitting again, stops waiting for it (the backend
  // job itself keeps running; only this client stops polling).
  const jobAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => jobAbortRef.current?.abort();
  }, []);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastApiCenterRef = useRef<{
    longitude: number;
    latitude: number;
  } | null>(null);
  const isInteractingRef = useRef<boolean>(false);

  const onPointClick = useCallback((info: any) => {
    if (info.object) {
      const point = info.object;
      setSelectedPoints((prev) => {
        const exists = prev.find((p) => p.name === point.name);
        if (exists) {
          return prev.filter((p) => p.name !== point.name);
        }
        return [...prev, point];
      });
    }
  }, []);

  const onGridClick = useCallback(
    (info: any) => {
      if (info.object && info.object.id) {
        const clickedGridId = info.object.id;

        let gridGeometry = odmGeometries.find((g) => g.geo_id === clickedGridId);
        if (!gridGeometry) {
          gridGeometry = selectedGrids.find((g) => g.geo_id === clickedGridId);
        }

        if (gridGeometry) {
          const exists = selectedGrids.find((g) => g.geo_id === clickedGridId);
          if (exists) {
            setSelectedGrids((prev) =>
              prev.filter((g) => g.geo_id !== clickedGridId)
            );
            removeGridId(clickedGridId);
            setSelectionNotice(null);
          } else if (selectedGrids.length < 10) {
            setSelectedGrids((prev) => [...prev, gridGeometry!]);
            addGridId(clickedGridId);
            setSelectionNotice(null);
          } else {
            setSelectionNotice("You can select at most 10 areas on the map. Remove one to add another.");
          }
        }
      }
    },
    [odmGeometries, selectedGrids, addGridId, removeGridId]
  );

  const handleGridRemove = useCallback(
    (gridId: string) => {
      setSelectedGrids((prev) => prev.filter((g) => g.geo_id !== gridId));
      removeGridId(gridId);
      setSelectionNotice(null);
    },
    [removeGridId]
  );

  const handleSubmitJob = useCallback(async () => {
    // The Submit button is disabled with the reason underneath it; this is the same check again.
    if (blockReason || !dw.window || !dw.day || !countryCode || !geoType) return;

    const cap = geoCapability(countryCode, 'odm', geoType);
    if (!cap) return;

    // Ids go out in the type the registry names for the area type; an id that cannot be
    // converted is refused here, not sent as null.
    const converted = toApiGeoIds(cap.idType, areaIds);
    if (!converted.ok) {
      setSubmitError({ title: "Can't submit these areas", body: converted.reason });
      return;
    }

    // Stop waiting for any earlier job, then clear previous results and remember the window this job is run for
    jobAbortRef.current?.abort();
    const controller = new AbortController();
    jobAbortRef.current = controller;
    setSubmitError(null);
    setJobResults(null);
    setJobStatus(null);
    setCurrentJobId(null);
    // The instants that go into the request are the ones kept with the job (the CSV states them).
    const windowUtc = { start: dw.window.start_time, end: dw.window.end_time };
    setSubmitted({ day: dw.day, granularity: dw.granularity, windowUtc });

    try {
      setIsSubmitting(true);

      // Between is one job with the selection on both sides. To / from is two jobs, the
      // selection to anywhere and anywhere to the selection (demo build only, see above).
      const jobs: ODMJobParams[] = odmRequestSides(direction, converted.ids).map((sides) => ({
        agg_type: "odm",
        measure: "trips",
        start_time: windowUtc.start,
        end_time: windowUtc.end,
        activity_type: dw.granularity,
        origin_geo_type: geoType,
        origin_geo_ids: [...sides.origin],
        destination_geo_type: geoType,
        destination_geo_ids: [...sides.destination],
        country_code: countryCode,
        full: false, // previews only; full exports are an API feature
        ...(gridRungs.length && rung ? { rung } : {}),
      }));

      // The first job is the one the page follows (its id, its status); the rest only add rows.
      const outcomes = await Promise.all(
        jobs.map((params, i) =>
          runOdmJob(params, {
            signal: controller.signal,
            ...(i === 0
              ? {
                  onSubmitted: ({ jobId }: { jobId: string }) => {
                    setCurrentJobId(jobId);
                    setIsSubmitting(false);
                    setIsPolling(true);
                  },
                  onStatusDetail: setJobStatus,
                }
              : {}),
          }),
        ),
      );

      // The demo API answers at once and never reports a submitted job; the outcome has the id.
      const first = outcomes[0];
      setCurrentJobId(first.jobId);
      setJobResults(mergeOdmRows(outcomes.map((o) => o.rows)));
    } catch (error) {
      // Leaving the page or submitting again cancelled this job: nothing to show.
      if (isAbortError(error)) return;
      // A draft target says why it may fail, after the error itself.
      const body = [describeRequestError(error), cap.pending?.reason].filter(Boolean).join(' ');
      setSubmitError(
        error instanceof JobError && error.kind === 'bad-response'
          ? { title: "Couldn't read the results", body }
          : { title: "The job didn't finish", body },
      );
    } finally {
      if (!controller.signal.aborted) {
        setIsSubmitting(false);
        setIsPolling(false);
      }
    }
  }, [blockReason, dw.window, dw.day, dw.granularity, countryCode, geoType, areaIds, direction, gridRungs, rung]);

  const handlePreview = useCallback(() => {
    if (!jobResults || !currentJobId) return;

    const handoff: OdmHandoff = {
      jobId: currentJobId,
      jobResults: jobResults,
      jobStatus: jobStatus,
      selection,
      geoType,
      direction,
      // Handed back on the way home, so the map, country, day and resolution are all kept
      geometries: odmGeometries,
      countryCode: countryCode,
      // The day and resolution the job was submitted for, not the picker's current value
      preservedDay: submitted?.day,
      preservedGranularity: submitted?.granularity,
      windowUtc: submitted?.windowUtc ?? null,
      pickerSearch: asSearch(navState?.pickerSearch),
    };

    // This page's own history entry still holds the state it was opened with (the setup page's
    // selections, or an earlier job). Give it the job, the areas, the day and the resolution
    // first, in the very form the preview's Back button hands back, so the browser's Back button
    // lands on the same screen instead of an empty one.
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: odmBackState(handoff) }
    );
    navigate('/odm-preview', { state: handoff });
  }, [jobResults, currentJobId, jobStatus, navigate, location.pathname, location.search, location.hash, selection, geoType, direction, odmGeometries, countryCode, submitted, navState?.pickerSearch]);

  // Demo build only: hand the demo dashboard the origin cells to anchor on.
  const handleViewDashboard = useCallback(() => {
    if (!jobResults || !currentJobId) return;
    // The dashboard's toggle flips whether the anchor cells are origins (outflows) or
    // destinations (inflows). The anchor is the selection.
    const anchor = odmGeometries;
    navigate('/odm-dashboard', {
      state: {
        jobId: currentJobId,
        jobStatus,
        selectedGeometries: anchor.map((g: any) => ({ geo_id: g.geo_id, geometry: g.geometry })),
        selectedIds: anchor.map((g: any) => String(g.geo_id)),
        singleAreaMode: direction === 'to-from',
      },
    });
  }, [jobResults, currentJobId, jobStatus, navigate, odmGeometries, direction]);

  const hasCenterChangedSignificantly = useCallback((newViewState: any) => {
    if (!lastApiCenterRef.current) {
      return true;
    }

    const { longitude: oldLng, latitude: oldLat } = lastApiCenterRef.current;
    const { longitude: newLng, latitude: newLat } = newViewState;

    const threshold = 0.01 / Math.pow(2, newViewState.zoom - 10);

    const deltaLng = Math.abs(newLng - oldLng);
    const deltaLat = Math.abs(newLat - oldLat);

    return deltaLng > threshold || deltaLat > threshold;
  }, []);

  const onViewStateChange = useCallback((newViewState: any) => {
    setCurrentZoom(newViewState.zoom);
    setViewState(newViewState);

    isInteractingRef.current = true;

    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }

    interactionTimeoutRef.current = setTimeout(() => {
      isInteractingRef.current = false;
    }, 300);
  }, []);

  const getBoundingBox = useCallback(() => {
    if (!viewState || !viewState.width || !viewState.height) {
      return null;
    }

    const viewport = new WebMercatorViewport(viewState);

    const [west, south] = viewport.unproject([0, viewState.height]);
    const [east, north] = viewport.unproject([viewState.width, 0]);

    return {
      lng1: west,
      lat1: south,
      lng2: east,
      lat2: north,
    };
  }, [viewState]);

  useEffect(() => {
    let zoomTimer: ReturnType<typeof setTimeout> | undefined;
    const state = location.state as ODMPageState | null;

    const scope = odmScopeFromState(state);
    if (state && scope) {
      setSelection(scope.selection);
      setRequestedDirection(scope.direction);
      const cc = normalizeCountry(state.countryCode) ?? selectionCountry(scope.selection);
      if (cc) setCountryCode(cc);

      // Returning from the preview: put the job back as it was
      if (state.preserveState) {
        setCurrentJobId(state.jobId ?? null);
        setJobResults(state.jobResults ?? null);
        setJobStatus(state.jobStatus ?? null);
      }

      // One colour: the selection.
      const allGeometries = (scope.geometries as MapGeometry[]).map((g) => ({ ...g, color: 'blue' }));

      setOdmGeometries(allGeometries);

      // Zoom to the selection
      if (allGeometries.length > 0) {
        const framed = fitView(allGeometries.map((g) => g.geometry), cc);
        setCurrentZoom(framed.zoom);
        zoomTimer = setTimeout(() => {
          setViewState({
            longitude: framed.longitude,
            latitude: framed.latitude,
            zoom: framed.zoom,
            pitch: 0,
            bearing: 0,
          });
        }, 150);
      }
    }

    return () => { if (zoomTimer) clearTimeout(zoomTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const debouncedFetchMapData = useCallback(
    (
      boundingBox: BoundingBox,
      center: { longitude: number; latitude: number }
    ) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (!isInteractingRef.current) {
          lastApiCenterRef.current = center;
        }
      }, 500);
    },
    []
  );

  useEffect(() => {
    // Don't auto-fetch map data if we have preset origin/destination geometries
    if (selection.length > 0) {
      return;
    }

    if (currentZoom > 13 && viewState) {
      const boundingBox = getBoundingBox();

      if (boundingBox) {
        const center = {
          longitude: viewState.longitude,
          latitude: viewState.latitude,
        };

        if (hasCenterChangedSignificantly(viewState)) {
          debouncedFetchMapData(boundingBox as BoundingBox, center);
        }
      }
    } else if (currentZoom <= 13) {
      // Only clear data if we don't have preset geometries
      if (selection.length === 0) {
        clearData();
      }
      lastApiCenterRef.current = null;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    }
  }, [
    currentZoom,
    viewState,
    getBoundingBox,
    hasCenterChangedSignificantly,
    debouncedFetchMapData,
    clearData,
    selection,
  ]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="h-screen flex">
      <div className="flex-1 flex flex-col">
        <NavBar
          title={PRODUCT_LABELS.odm}
          showBackButton
          backTo={pickerPath('odm', asSearch(navState?.pickerSearch))}
          backState={{ selection }}
        />

        {/* Collapsible Sections */}
        <div className="flex-1 overflow-y-auto">

          {/* Map Section */}
          <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
            <input type="checkbox" defaultChecked />
            <div className="collapse-title text-lg font-medium flex items-center space-x-2">
              <span>🗺️ Interactive Map</span>
              <div className="badge badge-sm badge-outline">
                {pluralize(odmGeometries.length, 'area')} loaded
              </div>
              <div className="badge badge-sm badge-outline">
                {directionLabel(direction)}: {selectionText}
              </div>
            </div>
            <div className="collapse-content p-0">
              <div className="h-96">
                <Map
                  key={mapCountry}
                  initialView={mapInitialView}
                  viewState={viewState}
                  data={SAMPLE_DATA}
                  selectedPoints={selectedPoints}
                  onPointClick={onPointClick}
                  onViewStateChange={onViewStateChange}
                  geometries={odmGeometries}
                  loading={loading}
                  selectedGrids={selectedGrids}
                  onGridClick={onGridClick}
                />
              </div>
              {selectionNotice && (
                <div className="p-4">
                  <DataStateNotice tone="warn" title="Selection limit reached" body={selectionNotice} />
                </div>
              )}
            </div>
          </div>

          {/* Selection Summary */}
          <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
            <input type="checkbox" defaultChecked />
            <div className="collapse-title text-lg font-medium flex items-center space-x-2">
              <span>📍 Selected Areas</span>
            </div>
            <div className="collapse-content">
              <div className="p-4 bg-note-soft rounded-lg border border-note-line">
                <h3 className="font-semibold text-note-ink mb-2">🔵 {directionLabel(direction)}</h3>
                <p className="text-sm text-note-ink">{selectionText}</p>
                {selection.length > 0 && (
                  <p className="text-xs text-note-ink mt-1">{selection.map((a) => a.name).join(', ')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Job Submission Section */}
          <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
            <input type="checkbox" defaultChecked />
            <div className="collapse-title text-lg font-medium flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span>🚀 Submit {PRODUCT_LABELS.odm} Job</span>
              </div>
            </div>
            <div className="collapse-content">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                <div>
                  <span className="text-ink-muted">Product:</span>
                  <span className="ml-2 font-medium">{PRODUCT_LABELS.odm}</span>
                  {countryCode ? (
                    <span className="ml-2 align-middle">
                      <SourceBadge country={countryCode} product="odm" />
                      <DraftDataBadge country={countryCode} product="odm" geoType={geoType} />
                    </span>
                  ) : null}
                </div>
                <div>
                  <span className="text-ink-muted">Direction:</span>
                  <span className="ml-2 font-medium">{directionLabel(direction)}</span>
                </div>
                <div>
                  <span className="text-ink-muted">Measure:</span>
                  <span className="ml-2 font-medium">Trips</span>
                </div>
                <div>
                  <span className="text-ink-muted">Time resolution:</span>
                  <span className="ml-2 font-medium">{GRANULARITY_LABELS[dw.granularity]}</span>
                </div>
                <div>
                  <span className="text-ink-muted">Area type:</span>
                  <span className="ml-2 font-medium">
                    {geoType ? geoTypeLabel(geoType, countryCode) : '–'}
                  </span>
                </div>
              </div>

              {gridRungs.length && rung ? (
                <div className="mb-4 text-sm">
                  <label className="flex items-center gap-2">
                    <span className="text-ink-muted">Cell size:</span>
                    <select className="select select-bordered select-sm" value={rung} onChange={(e) => setChosenRung(e.target.value)}>
                      {gridRungs.map((r) => (
                        <option key={r} value={r}>{rungLabel(r)}</option>
                      ))}
                    </select>
                  </label>
                  <p className="text-xs text-ink-muted mt-1">
                    Trips are counted between cells of one size: the data only pairs cells of the same size.
                    {rungCells && rungCells.widened > 0
                      ? ` ${pluralize(rungCells.widened, "smaller cell")} of the area ${rungCells.widened === 1 ? "is" : "are"} read as the whole ${rungLabel(rung)} cell around ${rungCells.widened === 1 ? "it" : "them"}.`
                      : ""}
                  </p>
                </div>
              ) : null}

              {countryCode ? (
                <div className="mb-4">
                  <AboutThisData country={countryCode} product="odm" measure="trips" dates={deliveredDays(dw)} />
                </div>
              ) : null}

              {submitError && (
                <div className="mb-4">
                  <DataStateNotice tone="error" title={submitError.title} body={submitError.body} />
                </div>
              )}

              <div className="flex justify-between items-center">
                <div className="text-sm text-ink-muted">
                  {dw.day ? formatDayLabel(dw.day) : 'No date'} · {GRANULARITY_LABELS[dw.granularity]} · UTC
                </div>
                <button
                  className={`btn btn-primary btn-sm ${(isSubmitting || isPolling || !!blockReason) ? 'btn-disabled' : ''}`}
                  onClick={handleSubmitJob}
                  disabled={isSubmitting || isPolling || !!blockReason}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-xs"></span>
                      Submitting...
                    </>
                  ) : isPolling ? (
                    <>
                      <span className="loading loading-spinner loading-xs"></span>
                      Processing...
                    </>
                  ) : (
                    'Submit Job'
                  )}
                </button>
              </div>
              {blockReason && <p className="text-xs text-ink-muted mt-2">{blockReason}</p>}
            </div>
          </div>

          {/* Job Status Section */}
          {currentJobId && !jobResults && (
            <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
              <input type="checkbox" defaultChecked />
              <div className="collapse-title text-lg font-medium">📊 Job Status</div>
              <div className="collapse-content">
                <JobStatusPanel jobId={currentJobId} status={jobStatus} />
              </div>
            </div>
          )}

          {/* Empty Results Warning */}
          {jobResults && jobResults.length === 0 && currentJobId && (
            <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
              <input type="checkbox" defaultChecked />
              <div className="collapse-title text-lg font-medium flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span>⚠️ No trips found</span>
                  <div className="badge badge-warning badge-sm">Empty Dataset</div>
                </div>
              </div>
              <div className="collapse-content">
                <div className="p-4 bg-paper-100 text-ink rounded-lg border border-base-300">
                  <p className="text-sm mb-3">
                    No trips were published for this selection and day.
                  </p>
                  <div className="text-sm text-ink-muted">
                    <strong>Why, and what to try:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Routes with fewer than 5 trips are not published (privacy rule).</li>
                      <li>Try larger areas, or another delivery day.</li>
                      {pendingFor(countryCode, 'odm', geoType) ? <li>{pendingFor(countryCode, 'odm', geoType)?.reason}</li> : null}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Job Results Section */}
          {jobResults && jobResults.length > 0 && currentJobId && (
            <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
              <input type="checkbox" defaultChecked />
              <div className="collapse-title text-lg font-medium flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span>📈 {PRODUCT_LABELS.odm} Results</span>
                  <div className="badge badge-success badge-sm">
                    {jobResults.length === PREVIEW_ROW_LIMIT
                      ? `First ${PREVIEW_ROW_LIMIT} rows (preview limit)`
                      : pluralize(jobResults.length, 'result')}
                  </div>
                </div>
                <div className="text-xs text-ink-muted font-mono">
                  Job ID: {currentJobId.substring(0, 8)}...
                </div>
              </div>
              <div className="collapse-content">
                {submitted && (
                  <p className="text-sm text-ink-muted mb-3">
                    Results for {formatDayLabel(submitted.day)} · {GRANULARITY_LABELS[submitted.granularity]} · UTC
                  </p>
                )}
                {pickerMoved && (
                  <div className="mb-3">
                    <DataStateNotice
                      tone="info"
                      title="The day or time resolution has changed"
                      body="These results are still the ones submitted earlier. Submit again to get the day shown in the data window."
                    />
                  </div>
                )}
                <div className="mb-3 empty:hidden">
                  <ResultCapNotice rows={jobResults} />
                </div>
                {/* Action Buttons */}
                <div className="flex justify-end space-x-2 mb-4 pb-3 border-b border-base-300">
                  <button
                    className={`btn btn-outline btn-sm ${(!jobResults || jobResults.length === 0) ? 'btn-disabled' : ''}`}
                    disabled={!jobResults || jobResults.length === 0}
                    onClick={handlePreview}
                    title={
                      !jobResults ? 'Complete job to enable preview' :
                      jobResults.length === 0 ? 'No results to preview' :
                      'View results preview (first 100 rows)'
                    }
                  >
                    📋 Preview
                  </button>
                  {isDemoJobId(currentJobId) && (
                    <button
                      className={`btn btn-outline btn-sm ${(!jobResults || jobResults.length === 0) ? 'btn-disabled' : ''}`}
                      disabled={!jobResults || jobResults.length === 0}
                      onClick={handleViewDashboard}
                      title={!jobResults || jobResults.length === 0 ? 'Complete job to enable dashboard' : 'View ODM flow dashboard'}
                    >
                      📊 View Dashboard
                    </button>
                  )}
                </div>

                {jobStatus && (
                  <div className="mb-4">
                    <JobStatusPanel jobId={currentJobId} status={jobStatus} />
                  </div>
                )}

                {/* The raw answer, for people who work against the API; closed by default */}
                <details className="bg-paper-100 text-ink rounded-lg p-3 mb-4">
                  <summary className="cursor-pointer text-xs font-medium">Show the raw API response (first 2 rows)</summary>
                  <pre className="text-xs text-ink-muted overflow-x-auto max-h-32 mt-2">
{JSON.stringify({
  job_id: currentJobId,
  status: "completed",
  preview: jobResults.slice(0, 2)
}, null, 2)}
                  </pre>
                </details>
              </div>
            </div>
          )}

        </div>

      </div>

      <AggregationConfiguration
        selectedGrids={selectedGrids}
        onGridRemove={handleGridRemove}
        measureLabel="Trips"
        dataControl={<AvailableDatePicker dw={dw} />}
        direction={{
          value: direction,
          onChange: setRequestedDirection,
          unavailable: toFromAvailable ? undefined : { 'to-from': TO_FROM_PENDING },
        }}
      />
    </div>
  );
};

export default ODM;